/**
 * Validation & Alert Management Panel
 * 
 * Full admin UI for managing validation rules, severity levels,
 * alert behavior, override policies, rule testing, and audit logs.
 */

import { useState, useMemo } from "react";
import {
  useSafetyControl,
  type ValidationSeverityLevel,
  type ValidationCategory,
  type TriggerEvent,
  type ValidationRuleConfig,
  CATEGORY_OPTIONS,
  TRIGGER_OPTIONS,
} from "@/data/SafetyControlContext";
import { useRole, ROLES } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ShieldOff, ShieldCheck, AlertOctagon, AlertTriangle, Info,
  RotateCcw, Clock, History, Settings, Plus, Pencil, Copy, Archive,
  Trash2, Search, Bell, UserCheck, FlaskConical, Undo2, ChevronDown, ChevronRight,
  Eye, CheckCircle, XCircle, Filter,
} from "lucide-react";
import { toast } from "sonner";

/* ── Constants ── */

const SEVERITY_OPTIONS: { value: ValidationSeverityLevel; label: string; icon: React.ElementType; color: string }[] = [
  { value: "hard_block", label: "Hard Block", icon: AlertOctagon, color: "text-destructive" },
  { value: "soft_block", label: "Soft Block", icon: AlertTriangle, color: "text-warning" },
  { value: "informational", label: "Informational", icon: Info, color: "text-info" },
];

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(CATEGORY_OPTIONS.map(c => [c.value, c.label]));
const TRIGGER_LABELS: Record<string, string> = Object.fromEntries(TRIGGER_OPTIONS.map(t => [t.value, t.label]));

const SEVERITY_BADGE: Record<ValidationSeverityLevel, string> = {
  hard_block: "bg-destructive/10 text-destructive border-destructive/20",
  soft_block: "bg-warning/10 text-warning border-warning/20",
  informational: "bg-info/10 text-info border-info/20",
};

/* ── Rule Form Dialog ── */

interface RuleFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: ValidationRuleConfig | null;
  onSave: (data: Omit<ValidationRuleConfig, "createdAt" | "updatedAt" | "priority">) => void;
  title: string;
}

function RuleFormDialog({ open, onOpenChange, initial, onSave, title }: RuleFormProps) {
  const [form, setForm] = useState(() => ({
    code: initial?.code || "",
    label: initial?.label || "",
    description: initial?.description || "",
    category: (initial?.category || "sales") as ValidationCategory,
    triggerEvent: (initial?.triggerEvent || "general") as TriggerEvent,
    defaultSeverity: (initial?.defaultSeverity || "soft_block") as ValidationSeverityLevel,
    currentSeverity: (initial?.currentSeverity || "soft_block") as ValidationSeverityLevel,
    enabled: initial?.enabled ?? true,
    archived: initial?.archived ?? false,
    isSystem: initial?.isSystem ?? false,
    alertEnabled: initial?.alertEnabled ?? false,
    alertRecipientRoles: initial?.alertRecipientRoles || [],
    createdBy: initial?.createdBy || "Current User",
  }));

  const isEdit = !!initial;
  const canSave = form.code.trim() && form.label.trim() && form.description.trim();

  const handleSave = () => {
    if (!canSave) return;
    onSave(form as Omit<ValidationRuleConfig, "createdAt" | "updatedAt" | "priority">);
    onOpenChange(false);
  };

  const toggleRecipient = (role: string) => {
    setForm(prev => ({
      ...prev,
      alertRecipientRoles: prev.alertRecipientRoles.includes(role)
        ? prev.alertRecipientRoles.filter(r => r !== role)
        : [...prev.alertRecipientRoles, role],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm">{title}</DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit ? "Update this validation rule's configuration." : "Define a new validation rule."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Rule Code</Label>
              <Input
                value={form.code}
                onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "") }))}
                className="text-xs h-8"
                placeholder="CUSTOM_RULE"
                disabled={isEdit}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Rule Name</Label>
              <Input
                value={form.label}
                onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                className="text-xs h-8"
                placeholder="My Custom Rule"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Textarea
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              className="text-xs min-h-[60px]"
              placeholder="What this rule checks and why..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v as ValidationCategory }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value} className="text-xs">{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Trigger Event</Label>
              <Select value={form.triggerEvent} onValueChange={v => setForm(p => ({ ...p, triggerEvent: v as TriggerEvent }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRIGGER_OPTIONS.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Severity Level</Label>
            <Select value={form.currentSeverity} onValueChange={v => setForm(p => ({ ...p, currentSeverity: v as ValidationSeverityLevel, defaultSeverity: isEdit ? p.defaultSeverity : v as ValidationSeverityLevel }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SEVERITY_OPTIONS.map(s => {
                  const SIcon = s.icon;
                  return (
                    <SelectItem key={s.value} value={s.value} className="text-xs">
                      <span className="flex items-center gap-1.5"><SIcon className={`h-3 w-3 ${s.color}`} />{s.label}</span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Alert config */}
          <div className="border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Alert Generation</Label>
              <Switch checked={form.alertEnabled} onCheckedChange={v => setForm(p => ({ ...p, alertEnabled: v }))} />
            </div>
            {form.alertEnabled && (
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Notify Roles</Label>
                <div className="flex flex-wrap gap-1.5">
                  {ROLES.map(r => (
                    <Badge
                      key={r.key}
                      variant={form.alertRecipientRoles.includes(r.key) ? "default" : "outline"}
                      className="text-[10px] cursor-pointer"
                      onClick={() => toggleRecipient(r.key)}
                    >
                      {r.label}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="text-xs">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={!canSave} className="text-xs">{isEdit ? "Save Changes" : "Create Rule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Rule Test Dialog ── */

function RuleTestDialog({ open, onOpenChange, rules }: { open: boolean; onOpenChange: (o: boolean) => void; rules: ValidationRuleConfig[] }) {
  const [testEvent, setTestEvent] = useState<TriggerEvent>("general");
  const activeRules = rules.filter(r => r.enabled && !r.archived);
  const matchingRules = activeRules.filter(r => r.triggerEvent === testEvent || r.triggerEvent === "general");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><FlaskConical className="h-4 w-4" /> Rule Test / Preview</DialogTitle>
          <DialogDescription className="text-xs">Simulate which rules would fire for a given trigger event.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Trigger Event</Label>
            <Select value={testEvent} onValueChange={v => setTestEvent(v as TriggerEvent)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TRIGGER_OPTIONS.map(t => (
                  <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <p className="text-xs font-medium">{matchingRules.length} rules would execute</p>
            <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
              {matchingRules.length === 0 && (
                <p className="text-xs text-muted-foreground p-4 text-center">No active rules for this event.</p>
              )}
              {matchingRules.map(r => {
                const sevOpt = SEVERITY_OPTIONS.find(s => s.value === r.currentSeverity);
                const SevIcon = sevOpt?.icon || Info;
                return (
                  <div key={r.code} className="p-2.5 flex items-center gap-2.5">
                    <SevIcon className={`h-3.5 w-3.5 flex-shrink-0 ${sevOpt?.color || "text-muted-foreground"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{r.label}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{r.description}</p>
                    </div>
                    <Badge variant="outline" className={`text-[9px] ${SEVERITY_BADGE[r.currentSeverity]}`}>
                      {sevOpt?.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-[11px] text-muted-foreground bg-muted/50 p-2.5 rounded-md">
            <strong>Preview only</strong> — no data is modified. This shows which rules would trigger based on current configuration.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main Panel ── */

export function ValidationControlPanel() {
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const {
    safeMode, setSafeMode,
    rules, updateRuleSeverity, toggleRule, resetRules,
    addRule, editRule, archiveRule, restoreRule, deleteRule, duplicateRule,
    overrideLog, ruleChangeLog,
    alertThrottle, setAlertThrottle,
    overridePolicy, setOverridePolicy,
  } = useSafetyControl();

  const isAdmin = hasPerm("admin_edit");
  const [activeTab, setActiveTab] = useState("rules");
  const [ruleSearch, setRuleSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(CATEGORY_OPTIONS.map(c => c.value)));

  // Dialogs
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<ValidationRuleConfig | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [testDialog, setTestDialog] = useState(false);

  const stats = useMemo(() => ({
    total: rules.filter(r => !r.archived).length,
    enabled: rules.filter(r => r.enabled && !r.archived).length,
    hardBlocks: rules.filter(r => r.enabled && !r.archived && r.currentSeverity === "hard_block").length,
    softBlocks: rules.filter(r => r.enabled && !r.archived && r.currentSeverity === "soft_block").length,
    info: rules.filter(r => r.enabled && !r.archived && r.currentSeverity === "informational").length,
    archived: rules.filter(r => r.archived).length,
  }), [rules]);

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <ShieldCheck className="h-8 w-8 mx-auto mb-2" />
          <p className="text-sm">Validation control requires authorized admin access.</p>
        </CardContent>
      </Card>
    );
  }

  const activeRules = showArchived ? rules : rules.filter(r => !r.archived);
  const filteredRules = activeRules.filter(r => {
    if (categoryFilter !== "all" && r.category !== categoryFilter) return false;
    if (severityFilter !== "all" && r.currentSeverity !== severityFilter) return false;
    if (ruleSearch) {
      const q = ruleSearch.toLowerCase();
      return r.label.toLowerCase().includes(q) || r.code.toLowerCase().includes(q) || r.description.toLowerCase().includes(q);
    }
    return true;
  });

  const groupedRules: Record<string, ValidationRuleConfig[]> = {};
  filteredRules.forEach(r => {
    if (!groupedRules[r.category]) groupedRules[r.category] = [];
    groupedRules[r.category].push(r);
  });

  // Sort within each category by priority
  Object.values(groupedRules).forEach(arr => arr.sort((a, b) => a.priority - b.priority));

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const handleAdd = (data: Omit<ValidationRuleConfig, "createdAt" | "updatedAt" | "priority">) => {
    addRule(data);
    toast.success("Rule created");
  };

  const handleEdit = (data: Omit<ValidationRuleConfig, "createdAt" | "updatedAt" | "priority">) => {
    editRule(data.code, data);
    toast.success("Rule updated");
  };

  const handleDelete = (code: string) => {
    const success = deleteRule(code);
    if (success) toast.success("Rule deleted");
    else toast.error("System rules cannot be deleted");
    setDeleteConfirm(null);
  };

  const ruleToDelete = deleteConfirm ? rules.find(r => r.code === deleteConfirm) : null;


  return (
    <div className="space-y-6">
      {/* Safe Mode Banner */}
      <Card className={safeMode ? "border-warning/50 bg-warning/5" : ""}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {safeMode ? <ShieldOff className="h-5 w-5 text-warning" /> : <ShieldCheck className="h-5 w-5 text-success" />}
              <div>
                <p className="font-medium text-sm">{safeMode ? "Safe Mode Active" : "Normal Mode"}</p>
                <p className="text-xs text-muted-foreground">
                  {safeMode ? "All validations are advisory only — no actions will be blocked" : "Validation rules are enforced based on configured severity levels"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="safe-mode" className="text-xs font-medium">Safe Mode</Label>
              <Switch
                id="safe-mode"
                checked={safeMode}
                onCheckedChange={(checked) => {
                  setSafeMode(checked);
                  toast[checked ? "warning" : "success"](checked ? "Safe Mode enabled — all checks are now advisory" : "Safe Mode disabled — enforcement restored");
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {[
          { label: "Total Rules", value: stats.total, color: "text-foreground" },
          { label: "Enabled", value: stats.enabled, color: "text-success" },
          { label: "Hard Blocks", value: stats.hardBlocks, color: "text-destructive" },
          { label: "Soft Blocks", value: stats.softBlocks, color: "text-warning" },
          { label: "Informational", value: stats.info, color: "text-info" },
          { label: "Archived", value: stats.archived, color: "text-muted-foreground" },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-3 text-center">
              <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto gap-0.5">
          <TabsTrigger value="rules" className="text-xs gap-1.5"><Settings className="h-3.5 w-3.5" /> Rules</TabsTrigger>
          <TabsTrigger value="alerts" className="text-xs gap-1.5"><Bell className="h-3.5 w-3.5" /> Alert Behavior</TabsTrigger>
          <TabsTrigger value="overrides" className="text-xs gap-1.5"><UserCheck className="h-3.5 w-3.5" /> Override Policy</TabsTrigger>
          <TabsTrigger value="throttle" className="text-xs gap-1.5"><Clock className="h-3.5 w-3.5" /> Throttling</TabsTrigger>
          <TabsTrigger value="override-log" className="text-xs gap-1.5"><History className="h-3.5 w-3.5" /> Override Log ({overrideLog.length})</TabsTrigger>
          <TabsTrigger value="change-log" className="text-xs gap-1.5"><Eye className="h-3.5 w-3.5" /> Change Log ({ruleChangeLog.length})</TabsTrigger>
        </TabsList>

        {/* ═══ RULES TAB ═══ */}
        <TabsContent value="rules" className="space-y-4 mt-4">
          {/* Toolbar */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search rules..."
                value={ruleSearch}
                onChange={e => setRuleSearch(e.target.value)}
                className="pl-8 text-xs h-8"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-8 text-xs w-[140px]"><Filter className="h-3 w-3 mr-1" /><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Categories</SelectItem>
                {CATEGORY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value} className="text-xs">{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="h-8 text-xs w-[130px]"><SelectValue placeholder="Severity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Severities</SelectItem>
                {SEVERITY_OPTIONS.map(s => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <Checkbox id="show-archived" checked={showArchived} onCheckedChange={v => setShowArchived(!!v)} />
              <Label htmlFor="show-archived" className="text-xs">Archived</Label>
            </div>
            <div className="ml-auto flex gap-1.5">
              <Button size="sm" variant="outline" className="text-xs h-8 gap-1" onClick={() => setTestDialog(true)}>
                <FlaskConical className="h-3.5 w-3.5" /> Test Rules
              </Button>
              <Button size="sm" variant="outline" className="text-xs h-8 gap-1" onClick={() => { resetRules(); toast.info("Rules reset to defaults"); }}>
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </Button>
              <Button size="sm" className="text-xs h-8 gap-1" onClick={() => setAddDialog(true)}>
                <Plus className="h-3.5 w-3.5" /> Add Rule
              </Button>
            </div>
          </div>

          {/* Grouped Rules */}
          {Object.entries(groupedRules).map(([category, catRules]) => (
            <Card key={category}>
              <div
                className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => toggleCategory(category)}
              >
                <div className="flex items-center gap-2">
                  {expandedCategories.has(category) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  <span className="text-sm font-medium">{CATEGORY_LABELS[category] || category}</span>
                  <Badge variant="secondary" className="text-[10px]">{catRules.length}</Badge>
                </div>
              </div>
              {expandedCategories.has(category) && (
                <CardContent className="p-0 pt-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Rule</TableHead>
                        <TableHead className="text-xs w-[100px]">Trigger</TableHead>
                        <TableHead className="text-xs w-[130px]">Severity</TableHead>
                        <TableHead className="text-xs w-[70px] text-center">Active</TableHead>
                        <TableHead className="text-xs w-[110px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {catRules.map(rule => {
                        const modified = rule.currentSeverity !== rule.defaultSeverity;
                        return (
                          <TableRow key={rule.code} className={`${!rule.enabled ? "opacity-50" : ""} ${rule.archived ? "bg-muted/30" : ""}`}>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium">{rule.label}</span>
                                {modified && <Badge variant="outline" className="text-[9px]">Modified</Badge>}
                                {rule.archived && <Badge variant="secondary" className="text-[9px]">Archived</Badge>}
                                {!rule.isSystem && <Badge variant="outline" className="text-[9px] border-primary/30 text-primary">Custom</Badge>}
                              </div>
                              <p className="text-[11px] text-muted-foreground">{rule.description}</p>
                              <p className="text-[10px] text-muted-foreground/60 font-mono">{rule.code}</p>
                            </TableCell>
                            <TableCell>
                              <span className="text-[11px] text-muted-foreground">{TRIGGER_LABELS[rule.triggerEvent] || rule.triggerEvent}</span>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={rule.currentSeverity}
                                onValueChange={v => updateRuleSeverity(rule.code, v as ValidationSeverityLevel)}
                                disabled={!rule.enabled || rule.archived}
                              >
                                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {SEVERITY_OPTIONS.map(opt => {
                                    const SevIcon = opt.icon;
                                    return (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        <span className="flex items-center gap-1.5">
                                          <SevIcon className={`h-3 w-3 ${opt.color}`} />
                                          {opt.label}
                                        </span>
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch
                                checked={rule.enabled}
                                onCheckedChange={() => toggleRule(rule.code)}
                                disabled={rule.archived}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-0.5">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditDialog(rule)} title="Edit">
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { duplicateRule(rule.code); toast.success("Rule duplicated"); }} title="Duplicate">
                                  <Copy className="h-3 w-3" />
                                </Button>
                                {rule.archived ? (
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { restoreRule(rule.code); toast.success("Rule restored"); }} title="Restore">
                                    <Undo2 className="h-3 w-3" />
                                  </Button>
                                ) : (
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { archiveRule(rule.code); toast.success("Rule archived"); }} title="Archive">
                                    <Archive className="h-3 w-3" />
                                  </Button>
                                )}
                                {!rule.isSystem && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteConfirm(rule.code)} title="Delete">
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              )}
            </Card>
          ))}

          {filteredRules.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No rules match your filters.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ ALERT BEHAVIOR TAB ═══ */}
        <TabsContent value="alerts" className="space-y-4 mt-4">
          <p className="text-xs text-muted-foreground">Configure which rules generate alerts and who receives them.</p>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Rule</TableHead>
                    <TableHead className="text-xs w-[80px]">Severity</TableHead>
                    <TableHead className="text-xs w-[80px] text-center">Alert</TableHead>
                    <TableHead className="text-xs">Recipient Roles</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.filter(r => !r.archived).map(rule => (
                    <TableRow key={rule.code} className={!rule.enabled ? "opacity-50" : ""}>
                      <TableCell>
                        <span className="text-xs font-medium">{rule.label}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[9px] ${SEVERITY_BADGE[rule.currentSeverity]}`}>
                          {SEVERITY_OPTIONS.find(s => s.value === rule.currentSeverity)?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={rule.alertEnabled}
                          onCheckedChange={v => editRule(rule.code, { alertEnabled: v })}
                        />
                      </TableCell>
                      <TableCell>
                        {rule.alertEnabled ? (
                          <div className="flex flex-wrap gap-1">
                            {rule.alertRecipientRoles.length > 0 ? rule.alertRecipientRoles.map(r => {
                              const roleInfo = ROLES.find(rl => rl.key === r);
                              return <Badge key={r} variant="secondary" className="text-[9px]">{roleInfo?.label || r}</Badge>;
                            }) : <span className="text-[11px] text-muted-foreground">No recipients</span>}
                          </div>
                        ) : <span className="text-[11px] text-muted-foreground">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ OVERRIDE POLICY TAB ═══ */}
        <TabsContent value="overrides" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Override Policy Configuration</CardTitle>
              <CardDescription className="text-xs">Define who can override Soft Blocks and what is required.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-xs font-medium">Roles Allowed to Override Soft Blocks</Label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map(r => (
                    <label key={r.key} className="flex items-center gap-1.5 text-xs">
                      <Checkbox
                        checked={overridePolicy.overrideRoles.includes(r.key)}
                        onCheckedChange={checked => {
                          const next = checked
                            ? [...overridePolicy.overrideRoles, r.key]
                            : overridePolicy.overrideRoles.filter(x => x !== r.key);
                          setOverridePolicy({ ...overridePolicy, overrideRoles: next });
                        }}
                      />
                      {r.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs">Require Reason Selection</Label>
                  <p className="text-[11px] text-muted-foreground">Users must select a reason when overriding.</p>
                </div>
                <Switch
                  checked={overridePolicy.requireReason}
                  onCheckedChange={v => setOverridePolicy({ ...overridePolicy, requireReason: v })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs">Require Notes</Label>
                  <p className="text-[11px] text-muted-foreground">Users must provide notes when overriding.</p>
                </div>
                <Switch
                  checked={overridePolicy.requireNotes}
                  onCheckedChange={v => setOverridePolicy({ ...overridePolicy, requireNotes: v })}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ THROTTLE TAB ═══ */}
        <TabsContent value="throttle" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Alert Deduplication & Throttling</CardTitle>
              <CardDescription className="text-xs">Control how frequently alerts repeat and whether related issues are grouped.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-xs">Deduplication Window: {alertThrottle.deduplicationWindowMinutes} minutes</Label>
                <Slider
                  value={[alertThrottle.deduplicationWindowMinutes]}
                  onValueChange={([v]) => setAlertThrottle({ ...alertThrottle, deduplicationWindowMinutes: v })}
                  min={15} max={480} step={15}
                />
                <p className="text-[11px] text-muted-foreground">Identical alerts within this window are deduplicated.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Max Repeat Alerts: {alertThrottle.maxRepeatAlerts}</Label>
                <Slider
                  value={[alertThrottle.maxRepeatAlerts]}
                  onValueChange={([v]) => setAlertThrottle({ ...alertThrottle, maxRepeatAlerts: v })}
                  min={1} max={10} step={1}
                />
                <p className="text-[11px] text-muted-foreground">Maximum times the same alert can appear before being suppressed.</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs">Group Related Issues</Label>
                  <p className="text-[11px] text-muted-foreground">Combine alerts for the same job/entity into a single grouped notification.</p>
                </div>
                <Switch
                  checked={alertThrottle.groupRelated}
                  onCheckedChange={v => setAlertThrottle({ ...alertThrottle, groupRelated: v })}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ OVERRIDE LOG TAB ═══ */}
        <TabsContent value="override-log" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Override Audit Log</CardTitle>
              <CardDescription className="text-xs">All validation overrides, blocks, and passes are logged here.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {overrideLog.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No validation events logged yet.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Time</TableHead>
                      <TableHead className="text-xs">Rule</TableHead>
                      <TableHead className="text-xs">Action</TableHead>
                      <TableHead className="text-xs">Outcome</TableHead>
                      <TableHead className="text-xs">Reason</TableHead>
                      <TableHead className="text-xs">User</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overrideLog.slice(0, 50).map(entry => (
                      <TableRow key={entry.id}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(entry.timestamp).toLocaleString()}</TableCell>
                        <TableCell className="text-xs">{entry.ruleLabel}</TableCell>
                        <TableCell className="text-xs capitalize">{entry.action.replace("_", " ")}</TableCell>
                        <TableCell>
                          <Badge variant={entry.outcome === "overridden" ? "default" : entry.outcome === "blocked" ? "destructive" : "secondary"} className="text-[10px]">
                            {entry.outcome}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate">{entry.reason}</TableCell>
                        <TableCell className="text-xs">{entry.role}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ CHANGE LOG TAB ═══ */}
        <TabsContent value="change-log" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Rule Change Audit Log</CardTitle>
              <CardDescription className="text-xs">All changes to validation rule configuration are tracked here.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {ruleChangeLog.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Eye className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No rule changes logged yet.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Time</TableHead>
                      <TableHead className="text-xs">User</TableHead>
                      <TableHead className="text-xs">Rule</TableHead>
                      <TableHead className="text-xs">Change</TableHead>
                      <TableHead className="text-xs">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ruleChangeLog.slice(0, 100).map(entry => (
                      <TableRow key={entry.id}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(entry.timestamp).toLocaleString()}</TableCell>
                        <TableCell className="text-xs">{entry.user}</TableCell>
                        <TableCell>
                          <div>
                            <span className="text-xs font-medium">{entry.ruleLabel}</span>
                            <p className="text-[10px] text-muted-foreground font-mono">{entry.ruleCode}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            entry.changeType === "deleted" ? "destructive" :
                            entry.changeType === "created" ? "default" : "secondary"
                          } className="text-[10px] capitalize">
                            {entry.changeType.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-[250px]">
                          {entry.before && entry.after ? (
                            <span className="text-muted-foreground">{entry.before} → {entry.after}</span>
                          ) : entry.after ? (
                            <span className="text-muted-foreground">{entry.after}</span>
                          ) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <RuleFormDialog open={addDialog} onOpenChange={setAddDialog} onSave={handleAdd} title="Add Validation Rule" />
      {editDialog && (
        <RuleFormDialog open={!!editDialog} onOpenChange={o => !o && setEditDialog(null)} initial={editDialog} onSave={handleEdit} title="Edit Validation Rule" />
      )}
      <RuleTestDialog open={testDialog} onOpenChange={setTestDialog} rules={rules} />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={o => !o && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-sm">Delete Rule</AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              Are you sure you want to permanently delete "{ruleToDelete?.label}"? This cannot be undone. Consider archiving instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction className="text-xs bg-destructive text-destructive-foreground" onClick={() => deleteConfirm && handleDelete(deleteConfirm)}>
              Delete Rule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
